import { useState, useEffect, useCallback, useRef } from 'react'
import { apiService } from '@/service/api'
import type {
  SheetConnector,
  CreateSheetConnectorRequest,
  UpdateSheetConnectorRequest,
  CreateWithMirrorResponse,
} from '@/types/sheet-connector'

interface UseSheetConnectorsReturn {
  connectors: SheetConnector[]
  loading: boolean
  error: Error | null
  create: (dto: CreateSheetConnectorRequest) => Promise<SheetConnector>
  createWithMirror: (dto: CreateSheetConnectorRequest) => Promise<CreateWithMirrorResponse>
  update: (id: string, dto: UpdateSheetConnectorRequest) => Promise<SheetConnector>
  remove: (id: string) => Promise<void>
  reload: () => void
}

export function useSheetConnectors(documentId: string | undefined): UseSheetConnectorsReturn {
  const [connectors, setConnectors] = useState<SheetConnector[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const fetchIdRef = useRef(0)

  const load = useCallback(() => {
    if (!documentId) {
      setConnectors([])
      setError(null)
      return
    }
    const id = ++fetchIdRef.current
    setLoading(true)
    setError(null)
    apiService
      .listSheetConnectors(documentId)
      .then((res) => {
        if (id === fetchIdRef.current) setConnectors(res.connectors)
      })
      .catch((err) => {
        if (id === fetchIdRef.current) {
          setConnectors([])
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })
      .finally(() => {
        if (id === fetchIdRef.current) setLoading(false)
      })
  }, [documentId])

  useEffect(() => {
    load()
  }, [load])

  const create = useCallback(
    async (dto: CreateSheetConnectorRequest): Promise<SheetConnector> => {
      const created = await apiService.createSheetConnector(dto)
      load()
      return created
    },
    [load],
  )

  const createWithMirror = useCallback(
    async (dto: CreateSheetConnectorRequest): Promise<CreateWithMirrorResponse> => {
      const result = await apiService.createSheetConnectorWithMirror(dto)
      load()
      return result
    },
    [load],
  )

  const update = useCallback(
    async (id: string, dto: UpdateSheetConnectorRequest): Promise<SheetConnector> => {
      const updated = await apiService.updateSheetConnector(id, dto)
      load()
      return updated
    },
    [load],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await apiService.deleteSheetConnector(id)
      load()
    },
    [load],
  )

  return { connectors, loading, error, create, createWithMirror, update, remove, reload: load }
}
